/**
 * Clause body format handling.
 *
 * Clause bodies come from `DocumentTemplate.template.body` in one of two forms:
 *   - **docx**: base64 of a `.docx` zip (the format all clauses save in going
 *     forward, once the HTML→DOCX migration lands). Magic bytes `PK` (0x50 0x4B).
 *   - **html**: legacy CKEditor HTML for clauses not yet re-saved as DOCX.
 *
 * The old container code base64-decoded blindly and ran binary DOCX through a
 * text decode, producing the garbled "encrypted-looking" string and a broken
 * insert. We instead classify the raw body and hand the insert path the right
 * representation: raw DOCX bytes → `insertDocxFragment`, or HTML → convert.
 */

export type ClauseBodyFormat = 'docx' | 'html';

/** Looks like a base64 blob (no HTML tags, only base64 alphabet). */
function isBase64(str: string): boolean {
  return str.length > 20 && /^[A-Za-z0-9+/\r\n]*={0,2}$/.test(str.trim());
}

/** Decode a base64 string to bytes (browser-safe, binary-correct). */
export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64.replace(/\s/g, ''));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * Classify a raw clause body. A base64 blob whose first bytes are the ZIP magic
 * (`PK`) is a DOCX; anything else is treated as HTML (the legacy path).
 */
export function classifyClauseBody(rawBody: string | undefined): ClauseBodyFormat {
  if (!rawBody) return 'html';
  if (!isBase64(rawBody)) return 'html';
  try {
    const bytes = base64ToBytes(rawBody);
    // ZIP local-file-header magic → a .docx package.
    if (bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b) return 'docx';
  } catch {
    /* fall through to html */
  }
  return 'html';
}

/**
 * Extract plain preview text from a base64 DOCX by reading the `<w:t>` runs out
 * of its `word/document.xml`. Cheap (one unzip + regex), fully client-side, and
 * gives clause items a real text preview instead of an opaque label. Returns ''
 * if the docx can't be read.
 */
export async function docxBase64ToPreviewText(b64: string): Promise<string> {
  try {
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(base64ToBytes(b64));
    const xml = await zip.file('word/document.xml')?.async('string');
    if (!xml) return '';
    // Join every <w:t>…</w:t> run; add spaces at paragraph boundaries so words
    // from adjacent paragraphs don't run together.
    const withBreaks = xml.replace(/<\/w:p>/g, ' </w:p>');
    const texts = [...withBreaks.matchAll(/<w:t[^>]*>([\s\S]*?)<\/w:t>/g)].map((m) => m[1]);
    return decodeXmlEntities(texts.join(''))
      .replace(/​/g, '') // strip zero-width spaces (CKE placeholder padding)
      .replace(/\s+/g, ' ')
      .trim();
  } catch {
    return '';
  }
}

function decodeXmlEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}
